import { useState } from 'react'
import type { Event, EventInput, EventType } from '../../api/types'
import { Button, ErrorNote, Field, Input, Select, Textarea } from '../../components/ui'
import { EVENT_TYPE_LABEL, fromLocalInput, toLocalInput } from '../../lib/format'

type Props = {
  initial?: Event
  onSubmit: (data: EventInput) => void
  pending: boolean
  error: unknown
  submitLabel: string
  onCancel?: () => void
}

export function EventForm({ initial, onSubmit, pending, error, submitLabel, onCancel }: Props) {
  const locked = initial?.locked ?? false
  const [f, setF] = useState({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    event_type: (initial?.event_type ?? 'FRUKOST') as EventType,
    starts_at: toLocalInput(initial?.starts_at),
    ends_at: toLocalInput(initial?.ends_at),
    location: initial?.location ?? '',
    address: initial?.address ?? '',
    capacity: initial?.capacity ?? 100,
    registration_closes_at: toLocalInput(initial?.registration_closes_at),
  })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((s) => ({ ...s, [k]: k === 'capacity' ? Number(e.target.value) : e.target.value }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      name: f.name.trim(),
      description: f.description.trim() || null,
      event_type: f.event_type,
      starts_at: fromLocalInput(f.starts_at)!,
      ends_at: fromLocalInput(f.ends_at),
      location: f.location.trim(),
      address: f.address.trim() || null,
      capacity: f.capacity,
      registration_closes_at: fromLocalInput(f.registration_closes_at),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {locked && (
        <div className="rounded-lg border border-warning-500/30 bg-warning-50 px-3 py-2 text-sm text-warning-600">
          Inbjudningar har skickats. Namn, tid, plats och typ är låsta (F-EV-02). Kapacitet och sista anmälningsdag kan fortfarande ändras.
        </div>
      )}
      <div className="grid gap-5 md:grid-cols-[2fr_1fr]">
        <Field label="Eventnamn">
          <Input required value={f.name} onChange={set('name')} placeholder="t.ex. Marknadsutsikter våren 2026" disabled={locked} />
        </Field>
        <Field label="Typ">
          <Select value={f.event_type} onChange={set('event_type')} disabled={locked}>
            {(Object.keys(EVENT_TYPE_LABEL) as EventType[]).map((t) => <option key={t} value={t}>{EVENT_TYPE_LABEL[t]}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Beskrivning" hint="Visas på anmälningssidan.">
        <Textarea value={f.description} onChange={set('description')} placeholder="Kort om innehållet och vem eventet riktar sig till." />
      </Field>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Start"><Input type="datetime-local" required value={f.starts_at} onChange={set('starts_at')} disabled={locked} /></Field>
        <Field label="Slut"><Input type="datetime-local" value={f.ends_at} onChange={set('ends_at')} disabled={locked} /></Field>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Plats"><Input required value={f.location} onChange={set('location')} placeholder="t.ex. Grand Hôtel, Spegelsalen" disabled={locked} /></Field>
        <Field label="Adress"><Input value={f.address} onChange={set('address')} placeholder="Gata, postnummer, ort" disabled={locked} /></Field>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Antal platser" hint="Anmälan stängs automatiskt när det är fullt.">
          <Input type="number" min={1} required value={f.capacity} onChange={set('capacity')} />
        </Field>
        <Field label="Sista anmälningsdag" hint="Lämna tomt för att tillåta anmälan fram till eventstart.">
          <Input type="datetime-local" value={f.registration_closes_at} onChange={set('registration_closes_at')} />
        </Field>
      </div>
      <ErrorNote error={error} />
      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && <Button type="button" onClick={onCancel}>Avbryt</Button>}
        <Button type="submit" variant="primary" loading={pending}>{submitLabel}</Button>
      </div>
    </form>
  )
}
